import { inject, InjectionToken } from '@angular/core';
import { addMonths, DateService, IsoDate, QueryFilter, Range } from '@softline/core';
import { translator } from '@softline/ui-core';
import {
  createDateRangeFilter,
  dateFilter,
  dateTimeFilter,
  entityFilter,
  filter,
  FilterState,
  numberFilter,
} from '@softapps/core/filter';
import { DeliveryNoteRow, DocumentParty, InvoiceRow } from './document-row';

export const SOFTLINE_FILTER_ABAU_INVOICES = new InjectionToken<FilterState<InvoiceRow>>(
  'SOFTLINE_FILTER_ABAU_INVOICES'
);
export const SOFTLINE_FILTER_ABAU_DELIVERY_NOTES = new InjectionToken<
  FilterState<DeliveryNoteRow>
>('SOFTLINE_FILTER_ABAU_DELIVERY_NOTES');

/**
 * Second state per list, holding the values the card's Einschränkungen set.
 *
 * It takes a state of its own because a node is resolved by field: the same field in
 * the shared state and in the extra one would be one node, and the second write would
 * overwrite the first instead of narrowing. Both states are ANDed into the list's query,
 * so the extra values can only narrow what the shared ones already selected.
 */
export const SOFTLINE_FILTER_ABAU_INVOICES_EXTRA = new InjectionToken<
  FilterState<InvoiceRow>
>('SOFTLINE_FILTER_ABAU_INVOICES_EXTRA');
export const SOFTLINE_FILTER_ABAU_DELIVERY_NOTES_EXTRA = new InjectionToken<
  FilterState<DeliveryNoteRow>
>('SOFTLINE_FILTER_ABAU_DELIVERY_NOTES_EXTRA');

const partyFormatter = (value: DocumentParty) => `${value.number} - ${value.name}`;

/** Baustellen of one Kunde share his Kundennummer - 236106 carries eleven - so only the
 *  Subnummer tells them apart. The tables render it too, so a cell and an option read the
 *  same. Without a Subnummer the party is the Kunde rather than a Baustelle and there is
 *  nothing to show; the option list never holds one. A Baustelle may carry no name at
 *  all, so the name is appended rather than templated - a dangling separator would
 *  otherwise end the label. */
export const formatSite = (value: DocumentParty): string => {
  if (!value.sub_number) return '';
  const number = `${value.number},${value.sub_number}`;
  return value.name ? `${number} - ${value.name}` : number;
};

/** `delivery_address` is the Kunde itself at Subnummer 0 and one of its Baustellen at
 *  every other Subnummer. The item picks Baustellen, so the Kunden are kept out of its
 *  option list - 6 of the 25 Lieferschein options, 71 of 400 on the Eingangsrechnungen.
 *  Options only: a row whose Baustelle is the Kunde still belongs in the list. */
const sitesOnly = <T extends object>(): QueryFilter<T> =>
  ({
    type: 'numberNotEquals',
    name: 'delivery_address.sub_number',
    value: 0,
  }) as unknown as QueryFilter<T>;

/** Each list opens on the last two months; it is the state's initialValue, so the period
 *  comes back with Zurücksetzen and the user can widen one list without the other.
 *  Months rather than days, so the window starts on the same day of the month. */
const defaultPeriod = (): Range<IsoDate> => {
  const to = inject(DateService).now() as IsoDate;
  return { from: addMonths(to, -2) as IsoDate, to };
};

export const invoicesFilter = () =>
  filter<InvoiceRow>({
    key: 'abau-invoice-link-invoices',
    initialValue: createDateRangeFilter<InvoiceRow>(
      'invoice_verification.document_date',
      defaultPeriod()
    ),
    factory: (ref) => {
      const t = translator();
      dateFilter(ref, {
        field: 'invoice_verification.document_date',
        title: t('#ABAU_INVOICE_LINK.FILTER.DATE_KIND_DOCUMENT'),
        icon: 'fa-regular fa-calendar-range',
        defaultOperator: 'between',
        allowedOperators: ['between'],
      });
      dateFilter(ref, {
        field: 'invoice_verification.period_date',
        title: t('#ABAU_INVOICE_LINK.FILTER.DATE_KIND_PERIOD'),
        icon: 'fa-regular fa-calendar-range',
        defaultOperator: 'between',
        allowedOperators: ['between'],
      });
      dateTimeFilter(ref, {
        field: 'invoice_verification.creation_time',
        title: t('#ABAU_INVOICE_LINK.FILTER.DATE_KIND_CREATION'),
        icon: 'fa-regular fa-calendar-range',
        defaultOperator: 'between',
        allowedOperators: ['between'],
      });
      numberFilter(ref, {
        field: 'invoice_verification.entry_count',
        title: t('#ABAU_INVOICE_LINK.FILTER.MIN_ENTRY_COUNT'),
        icon: 'fa-regular fa-list-ol',
        defaultOperator: 'greaterThanOrEqual',
        allowedOperators: ['greaterThanOrEqual'],
      });
      numberFilter(ref, {
        field: 'invoice_verification_values.value',
        title: t('#ABAU_INVOICE_LINK.FILTER.NET_RANGE'),
        icon: 'fa-regular fa-euro-sign',
        defaultOperator: 'between',
        allowedOperators: ['between', 'greaterThanOrEqual', 'lessThanOrEqual'],
      });
      entityFilter(ref, {
        field: 'supplier',
        title: t('#ABAU_INVOICE_LINK.FILTER.SUPPLIER'),
        icon: 'fa-regular fa-truck-field',
        additionalFields: ['number', 'name'],
        sortField: 'number',
        formatter: partyFormatter,
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
      entityFilter(ref, {
        field: 'delivery_address',
        title: t('#ABAU_INVOICE_LINK.FILTER.DELIVERY_ADDRESS'),
        icon: 'fa-regular fa-location-dot',
        additionalFields: ['number', 'sub_number', 'name'],
        sortField: 'number',
        formatter: formatSite,
        optionFilter: sitesOnly(),
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
      entityFilter(ref, {
        field: 'head_office',
        title: t('#ABAU_INVOICE_LINK.FILTER.HEAD_OFFICE'),
        icon: 'fa-regular fa-building',
        additionalFields: ['number', 'name'],
        sortField: 'number',
        formatter: partyFormatter,
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
    },
  });

export const deliveryNotesFilter = () =>
  filter<DeliveryNoteRow>({
    key: 'abau-invoice-link-delivery-notes',
    initialValue: createDateRangeFilter<DeliveryNoteRow>(
      'goods_receipt.document_date',
      defaultPeriod()
    ),
    factory: (ref) => {
      const t = translator();
      dateFilter(ref, {
        field: 'goods_receipt.document_date',
        title: t('#ABAU_INVOICE_LINK.FILTER.DATE_KIND_DOCUMENT'),
        icon: 'fa-regular fa-calendar-range',
        defaultOperator: 'between',
        allowedOperators: ['between'],
      });
      // Abrechnungsdatum has no kopfek counterpart - period_date is invoices only.
      dateTimeFilter(ref, {
        field: 'goods_receipt.creation_time',
        title: t('#ABAU_INVOICE_LINK.FILTER.DATE_KIND_CREATION'),
        icon: 'fa-regular fa-calendar-range',
        defaultOperator: 'between',
        allowedOperators: ['between'],
      });
      numberFilter(ref, {
        field: 'goods_receipt.entry_count',
        title: t('#ABAU_INVOICE_LINK.FILTER.MIN_ENTRY_COUNT'),
        icon: 'fa-regular fa-list-ol',
        defaultOperator: 'greaterThanOrEqual',
        allowedOperators: ['greaterThanOrEqual'],
      });
      // No Netto filter: the backend fills goods_receipt_values on no Lieferschein, so a
      // range could only ever match 0.00.
      entityFilter(ref, {
        field: 'supplier',
        title: t('#ABAU_INVOICE_LINK.FILTER.SUPPLIER'),
        icon: 'fa-regular fa-truck-field',
        additionalFields: ['number', 'name'],
        sortField: 'number',
        formatter: partyFormatter,
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
      entityFilter(ref, {
        field: 'delivery_address',
        title: t('#ABAU_INVOICE_LINK.FILTER.DELIVERY_ADDRESS'),
        icon: 'fa-regular fa-location-dot',
        additionalFields: ['number', 'sub_number', 'name'],
        sortField: 'number',
        formatter: formatSite,
        optionFilter: sitesOnly(),
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
      // Mitglied. kopfek has no head-office join; the same Ktostamm arrives as
      // billing_address and carries the identical id (236106 is 148065 on both).
      entityFilter(ref, {
        field: 'billing_address',
        title: t('#ABAU_INVOICE_LINK.FILTER.HEAD_OFFICE'),
        icon: 'fa-regular fa-building',
        additionalFields: ['number', 'name'],
        sortField: 'number',
        formatter: partyFormatter,
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
    },
  });

/** The parties a single list can be narrowed by on top of the shared selection. A pair
 *  does not have to share them: the link service accepts an invoice and a note with
 *  different Lieferant, Mitglied and Baustelle (probed 2026-10-06). */
export const invoicesExtraFilter = () =>
  filter<InvoiceRow>({
    key: 'abau-invoice-link-invoices-extra',
    factory: (ref) => {
      const t = translator();
      entityFilter(ref, {
        field: 'supplier',
        title: t('#ABAU_INVOICE_LINK.FILTER.SUPPLIER'),
        icon: 'fa-regular fa-truck-field',
        additionalFields: ['number', 'name'],
        sortField: 'number',
        formatter: partyFormatter,
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
      entityFilter(ref, {
        field: 'delivery_address',
        title: t('#ABAU_INVOICE_LINK.FILTER.DELIVERY_ADDRESS'),
        icon: 'fa-regular fa-location-dot',
        additionalFields: ['number', 'sub_number', 'name'],
        sortField: 'number',
        formatter: formatSite,
        optionFilter: sitesOnly(),
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
      entityFilter(ref, {
        field: 'head_office',
        title: t('#ABAU_INVOICE_LINK.FILTER.HEAD_OFFICE'),
        icon: 'fa-regular fa-building',
        additionalFields: ['number', 'name'],
        sortField: 'number',
        formatter: partyFormatter,
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
    },
  });

export const deliveryNotesExtraFilter = () =>
  filter<DeliveryNoteRow>({
    key: 'abau-invoice-link-delivery-notes-extra',
    factory: (ref) => {
      const t = translator();
      entityFilter(ref, {
        field: 'supplier',
        title: t('#ABAU_INVOICE_LINK.FILTER.SUPPLIER'),
        icon: 'fa-regular fa-truck-field',
        additionalFields: ['number', 'name'],
        sortField: 'number',
        formatter: partyFormatter,
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
      entityFilter(ref, {
        field: 'delivery_address',
        title: t('#ABAU_INVOICE_LINK.FILTER.DELIVERY_ADDRESS'),
        icon: 'fa-regular fa-location-dot',
        additionalFields: ['number', 'sub_number', 'name'],
        sortField: 'number',
        formatter: formatSite,
        optionFilter: sitesOnly(),
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
      entityFilter(ref, {
        field: 'billing_address',
        title: t('#ABAU_INVOICE_LINK.FILTER.HEAD_OFFICE'),
        icon: 'fa-regular fa-building',
        additionalFields: ['number', 'name'],
        sortField: 'number',
        formatter: partyFormatter,
        defaultOperator: 'in',
        allowedOperators: ['in'],
      });
    },
  });
